<template>
  <div class="article-info__wrapper">
    <div class="article-info__publisher-wrapper">
      <div class="article-info__publisher text-medium">
        <a-link
          v-if="publisherLinkProps"
          v-bind="publisherLinkProps"
          class="article-info__publisher-name bold"
        >
          {{ publisherName }}
        </a-link>
        <span v-else class="article-info__publisher-name bold">
          {{ publisherName }}
        </span>
      </div>
    </div>

    <div class="article-info__date-expandable-content-wrapper">
      <div class="article-info__date-wrapper">
        <div class="article-info__dot" />
        <a-article-info-date
          :published-on="publishedOn"
          class="article-info__date"
        />
      </div>

      <a-visibility
        v-if="showExpandableContent"
        show
        :on="breakpointsToShowExpandableContentOn"
      >
        <article-slot-expanded-content
          :expanded-content="expandedContent"
          :link-to-article="linkToArticle"
          class="article-info__expandable-content"
          @expanded-changed="handleExpandedChanged"
        />
      </a-visibility>
    </div>
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { pathOr } from 'ramda'
import { ROUTE_NAME } from 'enums/routes'
import mixins from '@/utils/mixins'
import { MIGRATED_CATEGORY_SLUG } from 'enums/categories'
import ArticleSlotExpandedContent from 'shared/AArticleSlot/ArticleSlotExpandedContent/index.vue'
import AArticleInfoDate from 'shared/AArticleInfoDate'
import scss from '@/utils/scss'

export default {
  name: 'ArticleSlotInfo',
  mixins: [mixins.urlGenerators],
  components: { ArticleSlotExpandedContent, AArticleInfoDate },
  props: {
    linkToArticle: propValidator([PROP_TYPES.STRING]),
    category: propValidator([PROP_TYPES.OBJECT], false),
    author: propValidator([PROP_TYPES.OBJECT], false),
    publishedOn: propValidator([PROP_TYPES.STRING]),
    sponsorshipCompany: propValidator([PROP_TYPES.OBJECT], false),
    showExpandableContent: propValidator([PROP_TYPES.BOOLEAN], false, true),
    expandedContent: propValidator([PROP_TYPES.STRING], false),
    breakpointsToShowExpandableContentOn: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => Object.values(scss.breakpoints)
    )
  },
  computed: {
    publisherLinkProps() {
      if (!this.sponsorshipCompany) {
        return {
          to: this.authorLink
        }
      }

      if (this.linkedCompany) {
        const { slug, categorySlug } = this.linkedCompany
        if (!slug || !categorySlug) return null

        return {
          to: this.companyUrl,
          trailingSlash: this.$helper.isLinkWithTrailingSlash(this.companyUrl)
        }
      } else {
        return null
      }
    },
    companyUrl() {
      if (!this.linkedCompany) return null

      return this.generateCompanyPath(this.linkedCompany)
    },
    authorLink() {
      if (!this.author || !this.author.slug) return null

      return {
        name: ROUTE_NAME.AUTHOR_AUTHOR,
        params: {
          author: this.author.slug
        }
      }
    },
    linkedCompany() {
      return this.sponsorshipCompany && this.sponsorshipCompany.linkedCompany
    },
    publisherName() {
      const authorName = this.author.name
      const companyName = pathOr(null, ['name'], this.sponsorshipCompany)

      return companyName || authorName
    },
    isNewsCategory() {
      return (
        this.category.slug.toLowerCase() ===
        MIGRATED_CATEGORY_SLUG.NEWS.toLowerCase()
      )
    }
  },
  methods: {
    handleExpandedChanged(isExpanded) {
      this.$emit('expanded-changed', isExpanded)
    }
  }
}
</script>

<style lang="scss" scoped>
$item-margin-top: 6px;

.article-info__wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  clear: both;

  // negative margin to make sure the elements have some gap
  // during wrapping
  margin-top: -#{$item-margin-top};

  .article-info__publisher-wrapper {
    margin-top: $item-margin-top;
    margin-right: 8px;
    display: flex;
    align-items: center;
  }

  .article-info__publisher {
    color: $c--gray-dark-2;
  }

  .article-info__publisher-name {
    color: $c--mid-purple;

    &:hover {
      color: $c--main;
      text-decoration: underline;
    }
  }

  .article-info__date-expandable-content-wrapper {
    display: flex;
    flex-grow: 1;
  }

  .article-info__date-wrapper {
    margin-top: $item-margin-top;
    display: flex;
    align-items: center;
  }

  .article-info__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    margin-right: 8px;
    background-color: $c--gray-dark;
    opacity: 0.3;
  }

  .article-info__expandable-content {
    margin-left: auto;
    margin-top: $item-margin-top;

    /deep/ .content-data__image {
      // to prevent overlapping between featured image and the
      // first image inside the expandable content
      clear: both;
    }
  }
}
</style>
